import React, { useCallback, useReducer, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CbmContext from './cbmContext';
import cbmReducer from './cbmReducer';
import authService from '../../Services/authService';
import {
  handleApiError,
  logError,
  createErrorDispatch,
} from '../../Components/Utils/errorHandler';
import {
  isSuccessResponse,
  isAuthValid,
} from '../../Components/Utils/apiHelpers';

import {
  ERROR_STATES,
  GET_PS,
  GET_LOGOUT,
  GET_STORES,
  ERROR_STORES,
  CLEAR_ERRORS,
  SET_LOADING,
  FORM_SUBMISSION,
  FORM_SUCCESS,
  TERM_TYPES,
  GET_AUTH,
  AUTH_ERROR,
  GET_USER,
  LOGIN_ERROR,
  GET_TARGET_ITEMS,
  GET_TARGET_ITEMS_PAGINATED,
  RESET_FORM_SUBMISSION_STATES,
  SET_SUBMIT_FAILED,
  SET_STORE_LOADING,
  SET_AUTH_LOADING,
  GET_US_STATES,
} from '../Types';
import { getBackendUrl } from '../../config/apiConfig';

const CbmState = (props) => {
  const backendUrl = getBackendUrl();
  const initialState = {
    stores: [],
    stateList: [],
    targetOrderItems: [],
    paginatedTargetItems: [],
    targetItemsPage: 0,
    targetItemsTotalPages: 0,
    targetItemsTotalElements: 0,
    targetItemsHasNext: false,
    targetItemsHasPrevious: false,
    auth: authService.getToken(),
    user: {},
    psList: [],
    isAuthenticated: false,
    errorMessage: '',
    errorCode: '',
    loginErrorMsg: '',
    submitStatus: false,
    submitFailed: false,
    submitSuccess: false,
    submittedData: null,
    retrieveStores: true,
    stateListRetrieved: false,
    psListRetrieved: false,
    loading: false,
    storeLoading: false,
    authLoading: false,
    cities: '',
    state: '',
    termType: null,
    success: false,
    logoutMsg: '',
    logoutType: '',
    headerLinks: [
      {
        link: '/?formName=backPay',
        title: 'BackPay',
      },
      {
        link: '/?formName=bonus',
        title: 'Bonus',
      },
      {
        link: '/?formName=hotel',
        title: 'Hotel',
      },
      {
        link: '/?formName=mileage',
        title: 'Mileage',
      },
      {
        link: '/?formName=nonMnMileage',
        title: 'Non MN Mileage',
      },
      {
        link: '/?formName=newHire',
        title: 'NewHire',
      },
      {
        link: '/?formName=perDiem',
        title: 'Perdiem',
      },
      {
        link: '/?formName=pto',
        title: 'Pto',
      },
      {
        link: '/?formName=targetOrder',
        title: 'Target Order',
      },
      {
        link: '/?formName=termination',
        title: 'Termination',
      },
      {
        link: '/?formName=timeAdjustment',
        title: 'Time Adjustment',
      },
      {
        link: '/?formName=uniform',
        title: 'Uniform',
      },
      {
        link: '/?formName=workTicket',
        title: 'Work Ticket',
      },
    ],
    portalUser: null,
  };

  const [state, dispatch] = useReducer(cbmReducer, initialState);

  const navigation = useNavigate();

  const clearErrors = useCallback(() => {
    setTimeout(() => dispatch({ type: CLEAR_ERRORS }), 3000);
  }, []);

  // Immediate error clear (used by ErrorBanner dismiss to avoid double-click)
  const clearErrorsImmediate = useCallback(() => {
    dispatch({ type: CLEAR_ERRORS });
  }, []);

  const clearSuccess = useCallback(() => {
    setTimeout(() => dispatch({ type: FORM_SUCCESS }), 3000);
  }, []);

  const setTermType = useCallback((termType) => {
    dispatch({
      type: TERM_TYPES,
      payload: termType,
    });
  }, []);

  const getPS = useCallback(async () => {
    if (!state?.portalUser?.id) {
      logError('getPS', new Error('Portal user ID not available'));
      return;
    }

    try {
      const res = await axios.get(
        `${backendUrl}/api/v1/ops/getPSByPortalUserId/${state.portalUser.id}`
      );

      if (res.status === 200 && res.data) {
        dispatch({
          type: GET_PS,
          payload: res.data,
          psListRetrieved: true,
          successMessage: 'PS list retrieved successfully.',
        });
      }
    } catch (err) {
      handleApiError(err, dispatch, ERROR_STORES, navigation, 'getPS');
    }
  }, [state?.portalUser?.id, backendUrl, navigation]);

  const getStates = useCallback(async () => {
    dispatch({ type: SET_LOADING });
    try {
      const res = await axios.get(`${backendUrl}/api/v1/statecity/getStates`);

      if (res.data) {
        dispatch({
          type: GET_US_STATES,
          payload: res.data,
          stateListRetrieved: true,
          loading: false,
        });
      }
    } catch (err) {
      handleApiError(err, dispatch, ERROR_STORES, navigation, 'getStates');
      dispatch({
        type: ERROR_STORES,
        retrieveStores: false,
        loading: false,
      });
    }
  }, [backendUrl, navigation]);

  // Generate a stable cache key that handles arrays and objects for store caching
  const getStoreCacheKey = useCallback((dist) => {
    if (Array.isArray(dist)) {
      // For arrays, extract IDs and sort for consistent cache key
      const ids = dist
        .map((d) =>
          typeof d === 'object' ? d.districtId || d.id || d.districtName : d
        )
        .filter(Boolean)
        .sort();
      return `stores_${ids.join('_')}`;
    }
    if (typeof dist === 'object' && dist !== null) {
      return `stores_${dist.districtId || dist.id || dist.districtName}`;
    }
    return `stores_${dist}`;
  }, []);

  const getStores = useCallback(
    async (district) => {
      if (!district) {
        logError('getStores', new Error('District parameter is required'));
        return;
      }

      // Handle empty arrays
      if (Array.isArray(district) && district.length === 0) {
        logError('getStores', new Error('District array is empty'));
        return;
      }

      // Check localStorage cache first
      const cacheKey = getStoreCacheKey(district);
      const cachedData = localStorage.getItem(cacheKey);
      const cacheTimestamp = localStorage.getItem(`${cacheKey}_timestamp`);
      const CACHE_DURATION = 60 * 60 * 1000; // 1 hour in milliseconds

      if (cachedData && cacheTimestamp) {
        const now = new Date().getTime();
        const cacheAge = now - parseInt(cacheTimestamp, 10);

        if (cacheAge < CACHE_DURATION) {
          // Use cached data
          try {
            const stores = JSON.parse(cachedData);
            dispatch({
              type: GET_STORES,
              payload: stores,
              storeLoading: false,
            });
            return;
          } catch (parseError) {
            logError('getStores', parseError);
            // If cache is corrupted, continue to fetch from API
          }
        }
      }

      // Fetch from API if no valid cache
      dispatch({ type: SET_STORE_LOADING, storeLoading: true });
      try {
        const res = await axios.post(
          `${backendUrl}/api/stores/v1/getDistrictStores`,
          district
        );

        if (res.data) {
          // Cache the response
          localStorage.setItem(cacheKey, JSON.stringify(res.data));
          localStorage.setItem(
            `${cacheKey}_timestamp`,
            new Date().getTime().toString()
          );

          dispatch({
            type: GET_STORES,
            payload: res.data,
            storeLoading: false,
          });
        }
      } catch (err) {
        handleApiError(err, dispatch, ERROR_STORES, navigation, 'getStores');
        dispatch({
          type: ERROR_STORES,
          retrieveStores: false,
          storeLoading: false,
        });
      }
    },
    [backendUrl, navigation, getStoreCacheKey]
  );

  const getTargetItems = useCallback(async () => {
    try {
      const items = await axios.get(`${backendUrl}/api/v1/ops/getTargetItems`);

      if (items.data?.targetItems) {
        dispatch({
          type: GET_TARGET_ITEMS,
          payload: items.data.targetItems,
        });
      }
    } catch (err) {
      handleApiError(err, dispatch, ERROR_STATES, navigation, 'getTargetItems');
    }
  }, [backendUrl, navigation]);

  const getTargetItemsPaginated = useCallback(
    async ({
      page = 0,
      size = 20,
      sortBy = 'itemName',
      sortDirection = 'asc',
    } = {}) => {
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          size: size.toString(),
          sortBy,
          sortDirection,
        });

        const res = await axios.get(
          `${backendUrl}/api/v1/ops/getTargetItemsPaginated?${params}`
        );

        if (res.data) {
          dispatch({
            type: GET_TARGET_ITEMS_PAGINATED,
            payload: res.data.content,
            page: res.data.number,
            totalPages: res.data.totalPages,
            totalElements: res.data.totalElements,
            hasNext: !res.data.last,
            hasPrevious: !res.data.first,
          });
        }
      } catch (err) {
        handleApiError(
          err,
          dispatch,
          ERROR_STATES,
          navigation,
          'getTargetItemsPaginated'
        );
      }
    },
    [backendUrl, navigation]
  );

  const clearStoresCache = useCallback(
    (district) => {
      if (district) {
        // Clear specific district cache using the same key format as getStores
        const cacheKey = getStoreCacheKey(district);
        localStorage.removeItem(cacheKey);
        localStorage.removeItem(`${cacheKey}_timestamp`);
      } else {
        // Clear all stores caches
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith('stores_')) {
            localStorage.removeItem(key);
          }
        });
      }
    },
    [getStoreCacheKey]
  );

  const logout = useCallback(async () => {
    const refreshToken = authService.getRefreshToken();

    // Call backend to revoke refresh token
    if (refreshToken) {
      try {
        await axios.post(`${backendUrl}/api/v1/auth/logout`, {
          refreshToken,
        });
      } catch (error) {
        console.error('Logout error:', error);
        // Continue with local logout even if backend call fails
      }
    }

    // Clear stores cache on logout
    clearStoresCache();
    authService.clearToken();
    dispatch({
      type: GET_LOGOUT,
      auth: null,
      logoutType: 'user logged out',
      logoutMsg: 'You have logged out successfully.',
    });
    navigation('/logout');
  }, [backendUrl, navigation, clearStoresCache]);
  // Validate User Token
  const validateToken = useCallback(
    async (token) => {
      if (!token) {
        logError('validateToken', new Error('Token is required'));
        navigation('/logout');
        return;
      }

      // Don't check if token is expired here - let axios interceptor handle refresh
      // The interceptor will automatically refresh expired tokens on 401 responses

      if (!state.authLoading) {
        dispatch({ type: SET_AUTH_LOADING, authLoading: true });
      }
      try {
        const res = await axios.get(
          `${backendUrl}/api/v1/auth/validateToken?token=${token}`
        );

        if (isAuthValid(res)) {
          // Update token if server sends new one
          if (res.data.jwt && res.data.jwt !== token) {
            authService.setToken(res.data.jwt, res.data.expiresIn);
          }

          dispatch({
            type: GET_AUTH,
            payload: res.data.jwtStatus,
            auth: res.data.jwt,
            user: res.data.user,
            portalUser: res.data.user,
            authLoading: false,
          });
          await Promise.resolve();
        } else {
          // Hard invalid token from server (not just expired) – logout
          authService.clearToken();
          authService.setLogoutFlag();
          dispatch({
            type: AUTH_ERROR,
            payload: false,
            auth: null,
            user: null,
            portalUser: null,
            authError: 'Invalid token',
            isAuthenticated: false,
            authLoading: false,
          });
          localStorage.setItem('goToLogout', 'true');
          navigation('/logout');
        }
      } catch (err) {
        // For 401/403, let axios interceptor handle refresh instead of forcing logout here
        const status = err.response?.status;

        if (status === 401 || status === 403) {
          // Stop the loading state but preserve current auth; interceptor will retry
          dispatch({
            type: SET_AUTH_LOADING,
            authLoading: false,
          });
          await Promise.resolve();
          // Do NOT clear tokens or navigate; interceptor should attempt refresh
          return;
        }

        // Non-auth or network/server errors: treat as hard failure and logout
        authService.clearToken();
        authService.setLogoutFlag();

        const errorDispatch = createErrorDispatch(err, 'validateToken');
        dispatch({
          type: AUTH_ERROR,
          ...errorDispatch,
          auth: null,
          user: null,
          portalUser: null,
          isAuthenticated: false,
          authLoading: false,
        });

        navigation('/logout');
      } finally {
        // Ensure tests and UI see loading flipped off regardless of path
        dispatch({
          type: SET_AUTH_LOADING,
          authLoading: false,
        });
        await Promise.resolve();
      }
    },
    [navigation, backendUrl, state.authLoading]
  );

  // Login User
  const loginUser = useCallback(
    async (userName, password) => {
      if (!userName || !password) {
        dispatch({
          type: LOGIN_ERROR,
          loginErrorMsg: 'Username and password are required',
          authLoading: false,
        });
        return null;
      }

      dispatch({ type: SET_AUTH_LOADING, authLoading: true });
      try {
        const res = await axios.post(`${backendUrl}/api/v1/auth/login`, {
          userName,
          password,
          credentials: 'include',
        });

        if (res.data?.jwt) {
          authService.setToken(res.data.jwt);

          // Store refresh token if provided
          if (res.data.refreshToken) {
            authService.setRefreshToken(res.data.refreshToken);
          }

          dispatch({
            type: GET_USER,
            payload: res.data.jwt,
            user: res.data.user,
            isAuthenticated: res.data.jwtStatus,
            portalUser: res.data.user,
            authLoading: false,
          });
          return res.data;
        } else {
          throw new Error('Invalid login response');
        }
      } catch (err) {
        logError('loginUser', err);
        // Clear all auth data on any error (especially 401 after backend restart)
        // This ensures stale tokens don't persist
        authService.clearToken();

        const errorDispatch = createErrorDispatch(err, 'loginUser');
        dispatch({
          type: LOGIN_ERROR,
          loginErrorMsg: errorDispatch.errorMessage,
          ...errorDispatch,
          authLoading: false,
        });
        return null;
      }
    },
    [backendUrl]
  );

  const formSubmit = useCallback(
    async (body, form, department) => {
      if (!body || !form || !department) {
        logError('formSubmit', new Error('Missing required parameters'));
        dispatch({
          type: ERROR_STATES,
          errorMessage: 'Invalid form submission. Please try again.',
          submitFailed: true,
          submitStatus: false,
          loading: false,
        });
        return;
      }

      dispatch({ type: SET_LOADING });
      try {
        const config = {};
        // Only set Content-Type for newHire, axios interceptor handles Authorization
        if (form === 'newHire') {
          config.headers = { 'Content-Type': 'multipart/form-data' };
        }

        const submittedForm = await axios.post(
          `${backendUrl}/api/v1/${department}/${form}Submit`,
          body,
          config
        );

        if (isSuccessResponse(submittedForm)) {
          dispatch({
            type: FORM_SUBMISSION,
            successMessage: 'Your form has been submitted successfully!',
            submitStatus: true,
            submitSuccess: true,
            success: true,
            loading: false,
            formId: submittedForm.data?.formId || null,
            pdfDownloadUrl: submittedForm.data?.pdfDownloadUrl || null,
            emailStatus: submittedForm.data?.emailStatus || null,
          });
          navigation('/success');
        } else {
          // API returned success=false
          dispatch({
            type: ERROR_STATES,
            payload: submittedForm.data,
            submitFailed: true,
            submitStatus: false,
            errorMessage:
              submittedForm.data?.apiError?.errorMessage ||
              'Form submission failed',
            errorCode: submittedForm.status,
            success: false,
            loading: false,
          });
        }
      } catch (err) {
        // handleApiError dispatches ERROR_STATES with error message and sets loading to false
        handleApiError(err, dispatch, ERROR_STATES, navigation, 'formSubmit');
        // Additional state updates not handled by handleApiError.
        // Do NOT overwrite existing friendly error fields here.
        dispatch({
          type: ERROR_STATES,
          submitFailed: true,
          submitStatus: false,
          submittedData: err.config?.data,
          success: false,
          loading: false,
        });
      }
    },
    [backendUrl, navigation]
  );

  const resetErrorCodeMsg = useCallback(() => {
    dispatch({
      type: ERROR_STATES,
      errorCode: false,
      errorMessage: false,
      submitStatus: false,
      submitFailed: false,
    });
  }, []);

  const setSubmitFailed = useCallback((valid) => {
    console.log('In submit failed');
    dispatch({
      type: SET_SUBMIT_FAILED,
      submitFailed: valid,
    });
  }, []);

  const resetSubmissionStates = useCallback(() => {
    dispatch({
      type: RESET_FORM_SUBMISSION_STATES,
      submitSuccess: false,
      submitFailed: false,
      submitStatus: false,
      // Also clear error states to prevent stale errors showing on success page
      errorMessage: null,
      userFriendlyMessage: null,
      errorReference: null,
      errorCode: null,
    });
  }, []);

  // Memoize context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo(
    () => ({
      user: state.user,
      stores: state.stores,
      stateList: state.stateList,
      errorMessage: state.errorMessage,
      errorCode: state.errorCode,
      userFriendlyMessage: state.userFriendlyMessage,
      errorReference: state.errorReference,
      loginErrorMsg: state.loginErrorMsg,
      submitStatus: state.submitStatus,
      submitFailed: state.submitFailed,
      submitSuccess: state.submitSuccess,
      submittedData: state.submittedData,
      isAuthenticated: state.isAuthenticated,
      loading: state.loading,
      storeLoading: state.storeLoading,
      authLoading: state.authLoading,
      auth: state.auth,
      retrieveStores: state.retrieveStores,
      success: state.success,
      successMessage: state.successMessage,
      psListRetrieved: state.psListRetrieved,
      stateListRetrieved: state.stateListRetrieved,
      headerLinks: state.headerLinks,
      termType: state.termType,
      psList: state.psList,
      portalUser: state.portalUser,
      targetOrderItems: state.targetOrderItems,
      paginatedTargetItems: state.paginatedTargetItems,
      targetItemsPage: state.targetItemsPage,
      targetItemsTotalPages: state.targetItemsTotalPages,
      targetItemsTotalElements: state.targetItemsTotalElements,
      targetItemsHasNext: state.targetItemsHasNext,
      targetItemsHasPrevious: state.targetItemsHasPrevious,
      logoutMsg: state.logoutMsg,
      logoutType: state.logoutType,
      lastSubmissionFormId: state.lastSubmissionFormId,
      lastSubmissionPdfUrl: state.lastSubmissionPdfUrl,
      lastSubmissionEmailStatus: state.lastSubmissionEmailStatus,
      loginUser,
      logout,
      getStores,
      clearStoresCache,
      getStates,
      getPS,
      clearErrors,
      clearErrorsImmediate,
      getTargetItems,
      getTargetItemsPaginated,
      validateToken,
      formSubmit,
      clearSuccess,
      setTermType,
      resetSubmissionStates,
      setSubmitFailed,
      resetErrorCodeMsg,
    }),
    [
      state,
      loginUser,
      logout,
      getStores,
      clearStoresCache,
      getStates,
      getPS,
      clearErrors,
      clearErrorsImmediate,
      getTargetItems,
      getTargetItemsPaginated,
      validateToken,
      formSubmit,
      clearSuccess,
      setTermType,
      resetSubmissionStates,
      setSubmitFailed,
      resetErrorCodeMsg,
    ]
  );

  return (
    <CbmContext.Provider value={contextValue}>
      {props.children}
    </CbmContext.Provider>
  );
};
export default CbmState;
